import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Address, countryDefaultValue } from '../../../../models/address/address';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { EntityData } from '@models/companies/entity-data';
import { SecurityHolding } from '@models/shares/security-holding';
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from '@models/shares/security-registry-record';
import { SecurityType } from '@models/shares/security-type';
import { NumbersValidators } from '../../../../shared/validators/numbers.validators';
import { DatePickerComponent } from '@shared/controls/date-picker/date-picker.component';
import { DisclaimerComponent } from '@shared/components/commons/disclaimer/disclaimer.component';
import { RadioComponent } from '@shared/controls/radio/radio.component';
import { SelectComponent } from '@shared/controls/select/select.component';
import { TextareaComponent } from '@shared/controls/textarea/textarea.component';
import { YesNoControlComponent } from '@shared/controls/yes-no-control-component/yes-no-control.component';
import { Guid } from '../../../../shared/utils/guid.helper';
import { BaseShareFormComponent } from '../base-share-form/base-share-form.component';
import {
  ShareEstimateTableComponent
} from '../../../reusable-form-parts/share-estimate-table/share-estimate-table.component';
import {
  CompanySecurityIssue,
  ConsiderationTypesOptions,
  ShareholderTypesEnum,
  ShareIssueStepsEnum
} from '../../../models/shares/company-security-issue.model';
import {
  AuthorisedSignatoriesStepComponent
} from '../../../reusable-form-parts/authorised-signatories-step/authorised-signatories-step.component';
import { IStep } from '@models/ui-models/step';
import {
  InputCertificateNumberComponent
} from '@shared/controls/input-certificate-number/input-certificate-number.component';
import { ClientSecurityTransaction } from '@models/security-transactions/client-security-transaction';
import { CompanySecurityIssueBlock } from "../company-security-issue-block/share-issue-block.component";
import { debounceTime } from "rxjs";
import { reloadShareholdersDebounceTimeMs } from "../constants/reload-shareholders-delay";
import { StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";
import { DatepickerHelper } from "../../../../shared/validators/date-picker-form-validators";
import { findCorrespondingRecord } from "../../../utils/find-corresponding-record";
import { CountriesService } from "../../../../shared/services/countries.service";

export const ShareIssueSteps: IStep<ShareIssueStepsEnum>[] = [
  { step: ShareIssueStepsEnum.FormDescription, label: 'Form Description' },
  { step: ShareIssueStepsEnum.Shareholders, label: 'Shareholders' },
  { step: ShareIssueStepsEnum.ShareIssuesAndCertificateDetails, label: 'Share Issues And Certificate Details' },
  { step: ShareIssueStepsEnum.AuthorisedSignatories, label: 'Authorised Signatories' },
  { step: ShareIssueStepsEnum.Estimate, label: 'Estimate' }
];

@Component({
  selector: 'app-share-issue',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SelectComponent,
    RadioComponent,
    YesNoControlComponent,
    TextareaComponent,
    DisclaimerComponent,
    StepperFormComponent,
    ShareEstimateTableComponent,
    DatePickerComponent,
    AuthorisedSignatoriesStepComponent,
    StepperFormDescriptionComponent,
    InputCertificateNumberComponent,
    CompanySecurityIssueBlock
  ],
  templateUrl: './company-security-issue.component.html',
  styleUrls: [
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
    '../company-security-cancellation/company-security-cancellation.component.scss'
  ]
})
export class CompanySecurityIssueComponent extends BaseShareFormComponent<ShareIssueStepsEnum, CompanySecurityIssue> implements OnInit {
  @Input() companySecurityStructures: SecurityHolding[] = [];
  @ViewChild(CompanySecurityIssueBlock) shareIssueBlock!: CompanySecurityIssueBlock;

  readonly considerationTypesOptions = ConsiderationTypesOptions;
  override readonly StepsEnum = ShareIssueStepsEnum;
  override steps = ShareIssueSteps;
  override currentStepIndex = 0;
  override currentStep = this.steps[0].step;

  transactionsList: ClientSecurityTransaction[] = [];

  securityTypes: SecurityType[] = [];

  override stepperForm = new FormGroup({
    [ShareIssueStepsEnum.FormDescription]: new FormGroup({}),
    [ShareIssueStepsEnum.Shareholders]: new FormGroup({
      date: new FormControl<Date | null>(null, [Validators.required]),
      ...CompanySecurityIssueBlock.defineFormControls(),
    }),
    [ShareIssueStepsEnum.ShareIssuesAndCertificateDetails]: new FormGroup({
      issueForCashConsidarationOnly: new FormControl<boolean | null>(true, [Validators.required]),
      nonCashConsiderationDetails: new FormControl<string | null>(null, [Validators.required]),
      sharesWereWrittenUnderContract: new FormControl<boolean | null>(false, [Validators.required]),
      shareCertificateNumber: new FormControl<string | null>(null, [NumbersValidators.isInteger]),
      consolidateCertificate: new FormControl<boolean | null>(false, [Validators.required]),
    }),
    [ShareIssueStepsEnum.AuthorisedSignatories]: new FormGroup({
      authorisedSignatories: AuthorisedSignatoriesStepComponent.defineForm(),
    }),
    [ShareIssueStepsEnum.Estimate]: new FormGroup({}),
  });

  constructor() {
    super();
    this.updateFormSteps();
    this.redirectAfterSubmit = false;
    this.shareholdersForm.controls.securityRegistryRecordGroup.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((record) => {
        this.updateFormSteps();

        if (record?.length) {
          const isNotBeneficialOwner = !(record[0].isBeneficialOwner ?? record[0].holders[0].details.isBeneficialOwner);
          const beneficialOwner = (record[0].beneficialOwner ?? record[0].holders[0].details.beneficialOwner ?? null);

          this.shareholdersForm.controls.isNotBeneficialOwner.setValue(isNotBeneficialOwner);
          this.shareholdersForm.controls.beneficialOwner.setValue(beneficialOwner);
        }
      });
    this.shareholdersForm.controls.securityTypeId.valueChanges
      .subscribe(() => this.updateFormSteps());
  }

  private updateFormSteps(): void {
    const steps = ShareIssueSteps.filter((step) => {
      if (step.step === ShareIssueStepsEnum.AuthorisedSignatories) {
        return this.isAuthorisedSignatoriesStepVisible();
      }

      return true;
    });

    this.steps = steps;
  }

  override setupFormChangeListeners(): void {
    super.setupFormChangeListeners();

    this.listenIssueForCashConsidarationOnlyChanges();
    this.listenShareholderDetailsFormChange();
    this.listenDateOfIssueChanges();
    this.listenChangeDateChange(this.shareholdersForm.controls.date, getForm484LateFeeNotification);
  }

  private listenIssueForCashConsidarationOnlyChanges(): void {
    this.issuesCertificateForm.controls.issueForCashConsidarationOnly.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((issueForCashConsidarationOnly) => {
        setControlDisabled(this.issuesCertificateForm.controls.nonCashConsiderationDetails, issueForCashConsidarationOnly !== false);
      });
  }

  private listenShareholderDetailsFormChange(): void {
    this.shareholdersForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.shouldLoadEstimate = true);
  }

  private listenDateOfIssueChanges(): void {
    this.shareholdersForm.controls.date.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs)
      )
      .subscribe((dateOfIssue) => {
        if (!dateOfIssue) return;

        this.loadShareholders(dateOfIssue, true);
      });
  }

  override setCurrentStep(newStepIndex: number): void {
    this.currentStep = this.steps[newStepIndex].step;
    this.currentStepIndex = newStepIndex;

    if (this.currentStep === ShareIssueStepsEnum.AuthorisedSignatories) {
      this.fillAuthorisedSignatories();
    }

    if (this.currentStep === ShareIssueStepsEnum.Estimate && this.shouldLoadEstimate) {
      this.loadEstimates();
    }

    this.updateFormSteps();
  }

  override fillAuthorisedSignatories(): void {
    const holderType = this.shareholdersForm.controls.shareholderType.value;

    if (holderType === ShareholderTypesEnum.Corporate) {
      const entityData = new EntityData({
        name: this.shareholdersForm.value.name ?? '',
        entityNumber: this.shareholdersForm.value.acn ?? '',
        registeredAddress: new Address(this.shareholdersForm.value.registeredAddress as Partial<Address>),
      });
      const newCorporateSecurityHolder = new CorporateHolderModel({ entityData });
      newCorporateSecurityHolder.isPosted = false;
      newCorporateSecurityHolder.details.authorisedSignatories = this.shareholdersForm.controls.authSignatories.value ?? [];

      this.authorisedSecurityRegistryRecords = [new SecurityRegistryRecord({
        holders: [newCorporateSecurityHolder],
        holdings: []
      })];
    } else if (this.shareholdersForm.controls.securityRegistryRecordGroup.value) {
      const record = this.shareholdersForm.controls.securityRegistryRecordGroup.value[0];
      this.authorisedSecurityRegistryRecords = [record];
    } else if (this.shareholdersForm.controls.joint.value) {
      const holders = this.shareholdersForm.controls.joint.value
        .filter(holder => holder.$type === CorporateHolderModel.$type)
        .map(holder => holder as CorporateHolderModel);

      this.authorisedSecurityRegistryRecords = holders.map(holder => {
        holder.isPosted = false;
        return new SecurityRegistryRecord({
          holders: [holder],
          holdings: [],
        });
      });
    }
  }

  override isAuthorisedSignatoriesStepVisible(): boolean {
    const holderType = this.shareholdersForm.controls.shareholderType.value;
    const holderGroup = this.shareholdersForm.controls.securityRegistryRecordGroup.value!;

    if (holderType === ShareholderTypesEnum.Corporate) {
      return true;
    } else if (holderType === ShareholderTypesEnum.ExistingShareholder) {
      if (!holderGroup) {
        return false;
      }

      const record = holderGroup[0];
      return record.holders.some((holder) => holder.$type == CorporateHolderModel.$type);
    } else if (holderType === ShareholderTypesEnum.JointShareholder) {
      const joints = this.shareholdersForm.controls.joint.value;
      return joints!.some(holder => holder.$type === CorporateHolderModel.$type);
    }

    return false;
  }

  override afterSubmit(changes: EntityChangeData[]) {
    const change = changes[0] as CompanySecurityIssue;
    change.issue.securityType.securityTypeId = this.shareholdersForm.value.securityTypeId ?? '';
    this.setupChange(change);
  }

  override setupChange(change: CompanySecurityIssue = this.formModel) {
    this.disableUnnecessaryControls();
    this.issuesCertificateForm.controls.issueForCashConsidarationOnly.setValue(this.issuesCertificateForm.value.issueForCashConsidarationOnly ?? null);

    if (!this.isEdit && change === this.formModel) {
      return;
    }

    this.shareIssueBlock.setupChange(change);

    this.shareholdersForm.patchValue({
      date: new Date(change.issue.transactionDate)
    });

    this.issuesCertificateForm.patchValue(change as any);
    this.issuesCertificateForm.patchValue(change.issue);

    this.cdr.detectChanges();
    this.issuesCertificateForm.controls.issueForCashConsidarationOnly.setValue(this.issuesCertificateForm.value.issueForCashConsidarationOnly ?? null);
    this.issuesCertificateForm.controls.sharesWereWrittenUnderContract.setValue(change.sharesWereWrittenUnderContract ?? null);
  }

  override onShareholdersLoadSuccess(records: SecurityRegistryRecord[], isReload = false) {
    this.securityRegistryRecords = records;
    this.isLoading = false;

    const allSecurityTypes = records.flatMap(shareholder => shareholder.holdings.map(holding => holding.securityType));
    this.securityTypes = [...new Map<string, SecurityType>(allSecurityTypes.map(type => [type.securityTypeId, type])).values()]
      .filter(type => type.entityId && type.entityId !== Guid.EmptyGuid);

    this.cdr.detectChanges();

    if (isReload) {
      const isExistingShareholderSelected = this.shareholdersForm.controls.shareholderType.value == ShareholderTypesEnum.ExistingShareholder;
      const selectedSecurityRegistryRecord = this.shareholdersForm.controls.securityRegistryRecordGroup.value;

      if (isExistingShareholderSelected && selectedSecurityRegistryRecord) {
        const matchingRecord = this.securityRegistryRecords.find((record) => selectedSecurityRegistryRecord[0].isEqual(record));
        const newValue = matchingRecord
          ? [matchingRecord]
          : null;

        this.shareholdersForm.controls.securityRegistryRecordGroup.setValue(newValue);
      } else {
        this.shareholdersForm.controls.securityRegistryRecordGroup.setValue(null);
      }
    } else {
      this.setupChange();
      this.prefillForm(this.prefillData, this.shouldPrefillSecurityType);
    }
  }

  override onEstimateLoadSuccess(estimatedSecurityRegistryRecords: SecurityRegistryRecord[]): void {
    this.estimatedSecurityRegistryRecords = estimatedSecurityRegistryRecords.filter(record => record?.holders.length > 0);
    this.shouldLoadEstimate = false;
  }

  override buildDocument(): Document | null {
    const companySecurityIssue = this.buildShareFormChange();

    // Document created that way because of FE models difficulties
    return {
      changes: [companySecurityIssue],
      entityId: this.companyChangeData?.entityId,
      type: 'c:484',
      documentId: this.companyChangeData?.documentId ?? Guid.EmptyGuid,
    } as any as Document;
  }

  override buildShareFormChange(): CompanySecurityIssue {
    const companySecurityIssue = this.shareIssueBlock.buildShareFormChange();

    companySecurityIssue.changeDate = this.shareholdersForm.value.date!;
    companySecurityIssue.issueForCashConsidarationOnly = this.issuesCertificateForm.value.issueForCashConsidarationOnly!;
    companySecurityIssue.nonCashConsiderationDetails = this.issuesCertificateForm.value.nonCashConsiderationDetails!;
    companySecurityIssue.consolidateCertificate = this.issuesCertificateForm.value.consolidateCertificate!;
    companySecurityIssue.sharesWereWrittenUnderContract = this.issuesCertificateForm.controls.sharesWereWrittenUnderContract.value!;

    companySecurityIssue.issue.transactionDate = this.shareholdersForm.value.date!;
    companySecurityIssue.issue.shareCertificateNumber = this.issuesCertificateForm.value.shareCertificateNumber ?? null;

    return companySecurityIssue;
  }

  override prefillForm(shareGroupView = this.prefillData, shouldPrefillSecurityType = this.shouldPrefillSecurityType): void {
    if (!shareGroupView?.shareholders?.length) {
      // group by class case
      return;
    }

    const callRecord = shareGroupView.callRecords[0];
    const record = callRecord?.record ?? null;
    const holders = shareGroupView.shareholders;
    const correspondingRecord = record
      ? findCorrespondingRecord(record, this.securityRegistryRecords)
      : null;
    this.shareholdersForm.controls.date.setValue(DatepickerHelper.getToday());

    if (correspondingRecord) {
      this.shareholdersForm.patchValue({
        shareholderType: ShareholderTypesEnum.ExistingShareholder,
        securityRegistryRecordGroup: record ? [record] : null,
      });
    } else if (holders.length) {

      if (holders.length > 1) {
        this.shareholdersForm.controls.shareholderType.setValue(ShareholderTypesEnum.JointShareholder);
        this.shareholdersForm.controls.joint.setValue(holders);
      } else if (holders[0].$type == IndividualHolderModel.$type && holders[0]['individualData']) {
        const individualHolderModel = holders[0] as IndividualHolderModel;
        this.shareholdersForm.controls.shareholderType.setValue(ShareholderTypesEnum.Individual);
        this.shareholdersForm.controls.newIndividualShareholder.patchValue({
          ...individualHolderModel.individualData,
          address: {
            ...individualHolderModel.address,
            country: individualHolderModel.address.country || countryDefaultValue
          }
        });
      } else if (holders[0].$type == CorporateHolderModel.$type && holders[0]['entityData']) {
        const corporateHolderModel = holders[0] as CorporateHolderModel;
        const country = corporateHolderModel.entityData.registeredAddress.country || countryDefaultValue;
        const isOverseasCompany = !CountriesService.isAustralianStateOrAustralia(country);
        this.shareholdersForm.patchValue({
          shareholderType: ShareholderTypesEnum.Corporate,
          name: corporateHolderModel.entityData.name.trim(),
          acn: corporateHolderModel.entityData.entityNumber ?? null,
          isOverseasCompany,
          registeredAddress: {
            ...corporateHolderModel.entityData.registeredAddress,
            country,
          }
        });
      }
    }

    if (shouldPrefillSecurityType) {
      this.shareholdersForm.controls.securityTypeId.setValue(record?.holdings?.[0]?.securityType?.securityTypeId ?? null);
    }

    super.prefillForm();
  }

  private disableUnnecessaryControls(): void {
    this.shareIssueBlock.disableUnnecessaryControls();
  }

  get shareholdersForm() {
    return this.stepperForm.controls[ShareIssueStepsEnum.Shareholders];
  }

  get issuesCertificateForm() {
    return this.stepperForm.controls[ShareIssueStepsEnum.ShareIssuesAndCertificateDetails];
  }

  get authorisedSignatoriesForm() {
    return this.stepperForm.controls[ShareIssueStepsEnum.AuthorisedSignatories];
  }
}
